import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { registerCharts, barOptions, barDataset, CHART } from '../components/ChartTheme';
import AppLayout from '../components/AppLayout';
import api from '../axios';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

registerCharts();

export default function AreaStartfy({ onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/startfy/dashboard')
      .then(res => setStats(res.data))
      .catch(err => setErro(errorMsg(err) || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, []);

  const kpis = [
    { icon: <PersonOutlineIcon />, value: stats?.alunos, label: 'Alunos' },
    { icon: <GroupsOutlinedIcon />, value: stats?.turmas, label: 'Turmas' },
    { icon: <SchoolOutlinedIcon />, value: stats?.professores, label: 'Professores' },
    { icon: <ClassOutlinedIcon />, value: stats?.aulas, label: 'Aulas' },
    { icon: <CheckCircleOutlineIcon />, value: stats?.matriculas_ativas, label: 'Matrículas' },
    { icon: <EmojiEventsIcon />, value: stats?.formandos, label: 'Formandos' },
  ];

  const chartLabels = ['Alunos', 'Turmas', 'Professores', 'Aulas', 'Matrículas', 'Formandos'];
  const chartValues = [stats?.alunos, stats?.turmas, stats?.professores, stats?.aulas, stats?.matriculas_ativas, stats?.formandos].map(v => v || 0);

  const atalhos = [
    { label: 'Alunos', icon: <PersonOutlineIcon />, path: '/alunos' },
    { label: 'Turmas', icon: <GroupsOutlinedIcon />, path: '/turmas' },
    { label: 'Aulas', icon: <ClassOutlinedIcon />, path: '/aulas' },
    { label: 'Frequências', icon: <CheckCircleOutlineIcon />, path: '/frequencias' },
    { label: 'Professores', icon: <SchoolOutlinedIcon />, path: '/professores' },
    { label: 'Matrículas', icon: <PeopleAltIcon />, path: '/matriculas' },
    { label: 'Formandos', icon: <EmojiEventsIcon />, path: '/formandos' },
    { label: 'Calendário', icon: <ClassOutlinedIcon />, path: '/calendario-aulas' },
  ];

  return (
    <AppLayout>
      <Box sx={{ py: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ color: CHART.teal, fontWeight: 700, letterSpacing: 1, mb: 0.5, textTransform: 'uppercase', fontSize: 12 }}>Módulo</Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2, fontWeight: 800 }}>Sistema Start</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 1 }}>Gestão escolar completa: alunos, turmas, professores, frequência e formandos.</Typography>
        </Box>

        {loading && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>}
        {erro && <Alert severity="error" sx={{ mb: 3 }}>{erro}</Alert>}

        {stats && (
          <>
            {/* KPIs */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {kpis.map((k, i) => (
                <Grid item xs={6} md={2} key={i}>
                  <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: 1, bgcolor: 'rgba(0,241,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: CHART.teal, flexShrink: 0 }}>{k.icon}</Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '4.4rem', color: 'text.primary', lineHeight: 1 }}>{k.value ?? '—'}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{k.label}</Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Chart (metrics) */}
            <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: CHART.teal }} />
                <Typography variant="h3" sx={{ fontSize: '1rem' }}>Visão geral do Start</Typography>
              </Box>
              <Box sx={{ height: 240 }}>
                <Bar
                  data={{ labels: chartLabels, datasets: [{ ...barDataset(chartValues), label: 'Registros' }] }}
                  options={barOptions()}
                />
              </Box>
            </Card>

            {/* Atalhos */}
            <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 2 }}>Atalhos</Typography>
            <Grid container spacing={1.5}>
              {atalhos.map(a => (
                <Grid item xs={12} sm={4} md={3} key={a.path}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(a.path)}
                    endIcon={<ArrowForwardIcon />}
                    fullWidth
                    sx={{ justifyContent: 'space-between', borderColor: 'rgba(0,241,167,0.4)', color: CHART.teal, fontWeight: 700, borderRadius: 1, px: 2, py: 1.2, '&:hover': { borderColor: CHART.teal, bgcolor: 'rgba(0,241,167,0.08)' } }}
                  >
                    {a.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </AppLayout>
  );
}
